import { ActivitySlots } from '@api/models';

// === Slot Processing Utilities ===

import { formatDateAsLocalString, parseUtcDateString } from './date';
import { convertUtcTimeSlotToLocal, formatTimeFromMinutes as formatTimeFromMinutesTimezone } from './timezone';

/**
 * Normalize a slot to a `timeSlots` array. New slots carry a single window
 * (startTime/endTime/price); legacy data may still expose `timeSlots`.
 */
function timeSlotsOf(slot: any): Array<{ startTime: number; endTime: number; price: number }> {
  if (Array.isArray(slot?.timeSlots) && slot.timeSlots.length > 0) return slot.timeSlots;
  if (typeof slot?.startTime === 'number' && typeof slot?.endTime === 'number') {
    return [{ startTime: slot.startTime, endTime: slot.endTime, price: slot.price ?? 0 }];
  }
  return [];
}

/**
 * Format duration in minutes to human-readable format
 * @param minutes Duration in minutes
 * @returns Formatted string like "30min", "1h 30min", "2h"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Extract minimum price from slot time slots
 * @param timeSlots Array of time slots with prices
 * @returns Minimum price from all time slots
 */
function getMinPriceFromTimeSlots(timeSlots: Array<{ startTime: number; endTime: number; price: number }>): number {
  if (!timeSlots || timeSlots.length === 0) return 0;
  return Math.min(...timeSlots.map((slot) => slot.price));
}

/**
 * Analyze slots to extract booking information
 * @param slots Array of slot data
 * @returns Processed slot information for booking UI
 */
export function analyzeSlotsForBooking(slots: ActivitySlots | undefined) {
  if (!slots || !Array.isArray(slots) || slots.length === 0) {
    return {
      singleSlot: null,
      groupSlot: null,
      rangeSlot: null
    };
  }

  // Separate slots by kind ('type' field)
  const singleSlots = slots.filter((slot: any) => slot.type === 'SINGLE');
  const groupSlots = slots.filter((slot: any) => slot.type === 'GROUP');
  const rangeSlots = slots.filter((slot: any) => slot.type === 'RANGE');

  const result: any = {};

  // Analyze open-range slots: RANGE is priced per-duration, so the entry price is
  // the smallest bookable duration's flat price (priceByDuration[minimal]). The
  // scalar slot.price already carries the cheapest per-duration price as a fallback.
  if (rangeSlots.length > 0) {
    let rangeMinDuration = Infinity;
    let rangeMinPrice = Infinity;
    rangeSlots.forEach((slot: any) => {
      const minimal = slot.fixedDurations?.length ? Math.min(...slot.fixedDurations) : 30;
      if (minimal < rangeMinDuration) rangeMinDuration = minimal;
      const entryPrice = slot.priceByDuration?.[minimal] ?? slot.price;
      if (typeof entryPrice === 'number' && entryPrice < rangeMinPrice) rangeMinPrice = entryPrice;
    });
    result.rangeSlot =
      rangeMinDuration !== Infinity
        ? {
            minDuration: formatDuration(rangeMinDuration),
            minPrice: rangeMinPrice === Infinity ? 0 : rangeMinPrice
          }
        : null;
  } else {
    result.rangeSlot = null;
  }

  // Analyze single slots
  if (singleSlots.length > 0) {
    let singleMinDuration = Infinity;
    let singleMinPrice = Infinity;
    let hasValidSingleSlot = false;

    singleSlots.forEach((slot: any) => {
      // Calculate duration from the slot window(s)
      const _ts = timeSlotsOf(slot);
      if (_ts.length > 0) {
        _ts.forEach((slot: any) => {
          if (typeof slot.startTime === 'number' && typeof slot.endTime === 'number') {
            const durationMinutes = slot.endTime - slot.startTime;
            if (durationMinutes > 0 && durationMinutes < singleMinDuration) {
              singleMinDuration = durationMinutes;
              hasValidSingleSlot = true;
            }
          }
        });

        const minPrice = getMinPriceFromTimeSlots(_ts);
        if (minPrice > 0 && minPrice < singleMinPrice) {
          singleMinPrice = minPrice;
          hasValidSingleSlot = true;
        }
      }
    });

    // Only create singleSlot object if we found valid slots
    if (hasValidSingleSlot && singleMinDuration !== Infinity) {
      result.singleSlot = {
        minDuration: formatDuration(singleMinDuration),
        minPrice: singleMinPrice === Infinity ? 0 : singleMinPrice
      };
    } else {
      result.singleSlot = null;
    }
  } else {
    result.singleSlot = null;
  }

  // Analyze group slots if they exist
  if (groupSlots.length > 0) {
    let groupMinDuration = Infinity;
    let groupMinPrice = Infinity;
    let groupMinPeople = Infinity;
    let hasValidGroupSlot = false;

    groupSlots.forEach((slot: any) => {
      // Calculate duration from the slot window(s)
      const _ts = timeSlotsOf(slot);
      if (_ts.length > 0) {
        _ts.forEach((slot: any) => {
          if (typeof slot.startTime === 'number' && typeof slot.endTime === 'number') {
            const durationMinutes = slot.endTime - slot.startTime;
            if (durationMinutes > 0 && durationMinutes < groupMinDuration) {
              groupMinDuration = durationMinutes;
              hasValidGroupSlot = true;
            }
          }
        });

        const minPrice = getMinPriceFromTimeSlots(_ts);
        if (minPrice > 0 && minPrice < groupMinPrice) {
          groupMinPrice = minPrice;
          hasValidGroupSlot = true;
        }
      }

      // Get minimum number of people (use maxPeople from schema)
      if (slot.maxPeople && slot.maxPeople > 1 && slot.maxPeople < groupMinPeople) {
        groupMinPeople = slot.maxPeople;
        hasValidGroupSlot = true;
      }
    });

    // Only create groupSlot object if we found valid slots
    if (hasValidGroupSlot && groupMinDuration !== Infinity) {
      result.groupSlot = {
        minDuration: formatDuration(groupMinDuration),
        minNumberOfPeople: groupMinPeople === Infinity ? 2 : groupMinPeople,
        minPrice: groupMinPrice === Infinity ? 0 : groupMinPrice
      };
    } else {
      result.groupSlot = null;
    }
  } else {
    result.groupSlot = null;
  }

  return result;
}

// Define the actual slot structure based on SlotEntity
export interface ActivitySlot {
  id: string;
  activityId: string;
  type: 'SINGLE' | 'GROUP';
  name: { [key: string]: string };
  maxPeople?: number;
  startDate: string; // ISO format date: YYYY-MM-DD
  repeat: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'WEEKDAYS' | 'NONE';
  repeatUntil?: string; // ISO format date: YYYY-MM-DD (end date for recurring slots)
  timeSlots: Array<{
    startTime: number;
    endTime: number;
    price: number;
  }>;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  createdAt: number;
  updatedAt: number;
}

export interface TimeSlot {
  id: string;
  time: string;
  price: string;
  available: boolean;
}

/**
 * Check if a slot applies to a given date based on its repeat pattern
 */
function isSlotValidForDate(slot: ActivitySlot, selectedDate: Date): boolean {
  const selectedDateStr = formatDateAsLocalString(selectedDate);

  // Parse slot date consistently
  const slotDate = parseUtcDateString(slot.startDate);

  // Check if this slot applies to the selected date
  if (slot.repeat === 'NONE') {
    return slot.startDate === selectedDateStr;
  }

  // For recurring slots, first check if selected date is past the repeatUntil end date
  if (slot.repeatUntil) {
    const repeatUntilDate = parseUtcDateString(slot.repeatUntil);
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const repeatUntilDateOnly = new Date(
      repeatUntilDate.getFullYear(),
      repeatUntilDate.getMonth(),
      repeatUntilDate.getDate()
    );
    if (selectedDateOnly > repeatUntilDateOnly) {
      return false;
    }
  }

  // For recurring slots, check if selected date matches the pattern
  switch (slot.repeat) {
    case 'DAILY': {
      const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const slotDateOnly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      return selectedDateOnly >= slotDateOnly;
    }
    case 'WEEKDAYS': {
      const selectedDateOnlyWd = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const slotDateOnlyWd = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      const dayOfWeek = selectedDate.getDay();
      return selectedDateOnlyWd >= slotDateOnlyWd && dayOfWeek >= 1 && dayOfWeek <= 5;
    }
    case 'WEEKLY': {
      // Match if selected date is on the same day of week and on or after slot start date
      const selectedDateOnlyWeekly = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      const slotDateOnlyWeekly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      return selectedDateOnlyWeekly >= slotDateOnlyWeekly && selectedDate.getDay() === slotDate.getDay();
    }
    case 'MONTHLY': {
      // Match if selected date is on the same day of month and on or after slot start date
      const slotDayOfMonth = slotDate.getDate();
      const selectedDayOfMonth = selectedDate.getDate();
      const daysInSelectedMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
      const adjustedSlotDay = Math.min(slotDayOfMonth, daysInSelectedMonth);

      const selectedDateOnlyMonthly = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      const slotDateOnlyMonthly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
      return selectedDateOnlyMonthly >= slotDateOnlyMonthly && selectedDayOfMonth === adjustedSlotDay;
    }
    default:
      return false;
  }
}

/**
 * Generate time slots from slot data for a specific date
 * Converts UTC time slots to local timezone
 */
export function getTimeSlotsForDate(
  slots: ActivitySlot[],
  selectedDate: Date,
  bookingType: 'SINGLE' | 'GROUP'
): TimeSlot[] {
  const timeSlots: TimeSlot[] = [];

  // Filter slots by type
  const filteredSlots = slots.filter((slot) => slot.type.toLowerCase() === bookingType.toLowerCase());

  filteredSlots.forEach((slot) => {
    const isDateMatch = isSlotValidForDate(slot, selectedDate);

    if (isDateMatch && slot.timeSlots && slot.timeSlots.length > 0) {
      slot.timeSlots.forEach((timeSlot, index) => {
        // Convert UTC time slot to local time, considering the selected date
        const localTimeSlot = convertUtcTimeSlotToLocal(timeSlot.startTime, timeSlot.endTime, selectedDate);

        // Format the local times
        const startTimeStr = formatTimeFromMinutesTimezone(localTimeSlot.startTime);
        const endTimeStr = formatTimeFromMinutesTimezone(localTimeSlot.endTime);

        // Note: If the date shifted due to timezone conversion, we might need to handle this
        // For now, we'll keep the time slot but developers should be aware of potential date shifts
        timeSlots.push({
          id: `${slot.id}-slot-${index}`,
          time: `${startTimeStr} - ${endTimeStr}`,
          price: timeSlot.price.toFixed(2),
          available: true
        });
      });
    }
  });

  // Sort by start time (using local time now)
  timeSlots.sort((a, b) => {
    const aStart = a.time.split(' - ')[0];
    const bStart = b.time.split(' - ')[0];
    return aStart.localeCompare(bStart);
  });

  return timeSlots;
}

/**
 * Find slot data for a given composite slot occurrence ID
 */
export function findSlotById(slots: ActivitySlot[], compositeSlotId: string): ActivitySlot | undefined {
  const baseSlotId = compositeSlotId.split('-slot-')[0];
  return slots.find((slot) => slot.id === baseSlotId);
}

/**
 * Convert TimeSlot object to display string format
 */
export function timeSlotToDisplayString(timeSlot: { startTime: number; endTime: number }): string {
  const startHour = Math.floor(timeSlot.startTime / 60);
  const startMin = timeSlot.startTime % 60;
  const endHour = Math.floor(timeSlot.endTime / 60);
  const endMin = timeSlot.endTime % 60;

  const startTimeStr = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
  const endTimeStr = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

  return `${startTimeStr} - ${endTimeStr}`;
}

/**
 * Format address object to comma-separated string
 * Generic formatter that works with any address-like object
 */
export function formatAddressToString(address: {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  zipCode?: string;
  country: string;
}): string {
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
    address.country
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Convert display string format to TimeSlot object
 */
export function displayStringToTimeSlot(timeString: string): { startTime: number; endTime: number } {
  const [startTimeStr, endTimeStr] = timeString.split(' - ');
  const [startHour, startMin] = startTimeStr.split(':').map(Number);
  const [endHour, endMin] = endTimeStr.split(':').map(Number);

  return {
    startTime: startHour * 60 + startMin,
    endTime: endHour * 60 + endMin
  };
}
